import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { COMPANY, CORPORATE_STRUCTURE, TECHNOLOGY_FOCUS, LEADERSHIP } from '../data/content';
import SEO from '../components/SEO';

const EntityCard = ({ entity, compact = false }) => (
  <div
    className={`relative rounded-2xl glass transition-all hover:bg-white/[0.05] ${
      compact ? 'p-5 md:p-6' : 'p-6 md:p-8'
    }`}
    data-testid={`structure-${entity.name.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-2">
      {entity.role}
    </div>
    <h3 className={`font-bold tracking-tight ${compact ? 'text-lg md:text-xl' : 'text-xl md:text-2xl'}`}>
      {entity.name}
    </h3>
    <p className={`text-white/55 leading-relaxed mt-3 ${compact ? 'text-xs md:text-sm' : 'text-sm'}`}>
      {entity.description}
    </p>
  </div>
);

const About = () => {
  return (
    <div className="bg-[#050505] text-white" data-testid="about-page">
      <SEO
        title="About Bronto Intelligence — Our Story, Mission, and Corporate Structure"
        description="Learn about Bronto Intelligence Private Limited — the Indian parent company behind BrontoAI, BrontoY, MSPConnect, and Bronto Cosmos. Our mission, corporate structure, and technology focus."
        path="/about"
      />
      {/* Hero */}
      <section className="relative pt-20 pb-24 px-6 lg:px-10 overflow-hidden">
        <div className="aurora-bg" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            About
          </div>
          <h1 className="text-display text-5xl md:text-7xl lg:text-8xl mb-10 max-w-5xl">
            An <span className="rainbow-text">Indian technology</span> company
            building intelligent platforms.
          </h1>
          <p className="text-xl md:text-2xl text-white/65 max-w-4xl leading-relaxed font-light">
            {COMPANY.description}
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
              Overview
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              The parent organization.
            </h2>
          </div>
          <div className="md:col-span-8 space-y-5">
            <p className="text-lg text-white/75 leading-relaxed">
              {COMPANY.longDescription}
            </p>
            <p className="text-base text-white/55 leading-relaxed">
              The company combines artificial intelligence, economics, digital infrastructure, and public-impact technology with the objective of building practical systems that improve access to information, decision-making, and technology-enabled services.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl glass p-10 md:p-12">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
              Mission
            </div>
            <p className="text-2xl md:text-3xl font-medium leading-snug tracking-tight">
              {COMPANY.mission}
            </p>
          </div>
          <div className="rounded-3xl glass p-10 md:p-12">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
              Vision
            </div>
            <p className="text-2xl md:text-3xl font-medium leading-snug tracking-tight">
              {COMPANY.vision}
            </p>
          </div>
        </div>
      </section>

      {/* Corporate Structure */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
              Corporate Structure
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
              How the Bronto ecosystem is organized.
            </h2>
          </div>

          {(() => {
            const parent = CORPORATE_STRUCTURE.find((e) => e.role === 'Parent Company');
            const platformsSubsidiary = CORPORATE_STRUCTURE.find((e) => e.name === 'Bronto Platforms Private Limited');
            const cosmosSubsidiary = CORPORATE_STRUCTURE.find((e) => e.name === 'Bronto Cosmos Private Limited');
            const platformsChildren = CORPORATE_STRUCTURE.filter((e) => ['BrontoAI', 'BrontoY', 'MSPConnect'].includes(e.name));
            const cosmosChildren = CORPORATE_STRUCTURE.filter((e) => e.name === 'Bronto Cosmos');

            return (
              <div className="relative">
                {/* Parent (centered, top) */}
                <div className="flex justify-center">
                  <div className="w-full md:max-w-2xl">
                    <EntityCard entity={parent} />
                  </div>
                </div>

                {/* Vertical trunk + horizontal branch (desktop only) */}
                <div className="hidden md:block relative h-16">
                  <div className="absolute left-1/2 top-0 h-8 w-px bg-white/15" />
                  <div className="absolute left-[25%] right-[25%] top-8 h-px bg-white/15" />
                  <div className="absolute left-[25%] top-8 h-8 w-px bg-white/15" />
                  <div className="absolute right-[25%] top-8 h-8 w-px bg-white/15" />
                </div>

                {/* Two parallel subsidiary branches */}
                <div className="mt-6 md:mt-0 grid md:grid-cols-2 gap-6 md:gap-8">
                  {/* Branch 1: Bronto Platforms */}
                  <div className="space-y-4">
                    <EntityCard entity={platformsSubsidiary} />
                    {/* Connector to children */}
                    <div className="hidden md:flex justify-center">
                      <div className="h-4 w-px bg-white/15" />
                    </div>
                    <div className="space-y-3 md:pl-6 md:border-l md:border-white/10 md:ml-6">
                      {platformsChildren.map((child) => (
                        <EntityCard key={child.name} entity={child} compact />
                      ))}
                    </div>
                  </div>

                  {/* Branch 2: Bronto Cosmos */}
                  <div className="space-y-4">
                    <EntityCard entity={cosmosSubsidiary} />
                    <div className="hidden md:flex justify-center">
                      <div className="h-4 w-px bg-white/15" />
                    </div>
                    <div className="space-y-3 md:pl-6 md:border-l md:border-white/10 md:ml-6">
                      {cosmosChildren.map((child) => (
                        <EntityCard key={child.name} entity={child} compact />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          <p className="text-xs text-white/35 leading-relaxed mt-10 max-w-4xl">
            Bronto Intelligence Private Limited is the parent organization behind BrontoAI, BrontoY, MSPConnect, and Bronto Cosmos. Bronto Platforms Private Limited and Bronto Cosmos Private Limited serve as the operating subsidiaries responsible for the development, operation, management, and commercialization of the company&apos;s digital platforms.
          </p>
        </div>
      </section>

      {/* Technology Focus */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
              Technology Focus
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
              Areas of work.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-3xl overflow-hidden">
            {TECHNOLOGY_FOCUS.map((item, idx) => (
              <div key={item.title} className="bg-[#050505] p-8 md:p-10 hover:bg-white/[0.03] transition-colors">
                <div className="text-xs font-semibold text-white/30 mb-3">0{idx + 1}</div>
                <h3 className="text-xl font-bold tracking-tight mb-3">{item.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Preview */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
              Leadership
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
              Strategic leadership.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {LEADERSHIP.map((leader) => (
              <Link
                key={leader.id}
                to={`/leadership/${leader.id}`}
                className="group glass rounded-3xl p-8 hover-lift block"
              >
                <div className="flex gap-5 items-start">
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-white/5 ring-1 ring-white/10 shrink-0">
                    {leader.photoUrl ? (
                      <img src={leader.photoUrl} alt={leader.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-lg font-bold text-white/30">
                        {leader.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold tracking-tight">{leader.name}</h3>
                    <p className="text-sm text-white/55 mt-1">{leader.title}</p>
                    <p className="text-sm text-white/65 leading-relaxed mt-3 line-clamp-2">
                      {leader.shortBio}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10">
            <Link to="/leadership" className="btn-secondary inline-flex">
              View Full Leadership
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Future Initiatives */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
            Future Initiatives
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight max-w-4xl mb-8">
            A long-term technology ecosystem.
          </h2>
          <p className="text-lg text-white/65 max-w-4xl leading-relaxed">
            Bronto Intelligence is committed to developing a long-term technology ecosystem that spans artificial intelligence, economic intelligence, agricultural technology, and future digital infrastructure. The company continues to invest in research, product development, and platforms designed to remain relevant and impactful across the next generation of digital experiences.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
